import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import AccessibilityIcon from "../../assets/svg/accessibility-icon";
import UserIcon from "../../assets/svg/user-icon";
import NotificationBellIcon from "../../assets/svg/notification-bell-icon";
import AccessibilityDrawer from "../AccessibilityDrawer/AccessibilityDrawer";
import ProfileCard from "../ProfileCard/ProfileCard";
import { RootState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { UserData } from "../../utils/global-types/global-types";
import AddIcon from "../../assets/svg/add-icon";
import QuickActionsPopup from "./components/QuickActionsPopup/QuickActionsPopup";
import EmailSignatureModal from "../EmailSignature/EmailSignatureModal";
import EmailSignatureDarkModePreview from "../EmailSignature/EmailSignatureDarkModePreview";
import { toggleDrawer } from "../../redux/slices/notificationSlice";
import AgentAvailabilityLeavesModal from "../AgentAvailabilityLeaves/AgentAvailabilityLeavesModal";
import AddContentDrawer from "../../pages/Playground/components/AddContentDrawer/AddContentDrawer";
import { useGetPropertiesListQuery } from "../../redux/services/playgroundApi";
import { useGetLeadFormFieldsQuery } from "../../redux/services/communicationApi";
import { setLeadsFormConfig } from "../../redux/slices/leadsSlice";

const Header = ({ children }: { children?: React.ReactNode }) => {
  const dispatch = useDispatch();
  const notificationState = useSelector(
    (store: RootState) => store?.notifications
  );
  const auth = useSelector((store: RootState) => store?.auth);
  const userData = useSelector((store: RootState) => store?.auth?.user);
  const [openAccessibilityDrawer, setOpenAccessibilityDrawer] = useState(false);
  const [openProfileCard, setOpenProfileCard] = useState(false);
  const [openQuickActions, setOpenQuickActions] = useState(false);
  const [openEmailSignatureModal, setOpenEmailSignatureModal] = useState(false);
  const [
    openEmailSignatureDarkModePreview,
    setOpenEmailSignatureDarkModePreview,
  ] = useState(false);
  const [previewSignatureHtml, setPreviewSignatureHtml] = useState("");
  const [
    openAgentAvailabilityLeavesModal,
    setOpenAgentAvailabilityLeavesModal,
  ] = useState(false);
  const [addContentDrawerOpen, setAddContentDrawerOpen] = useState(false);
  const quickActionsButtonRef = useRef<HTMLButtonElement>(null);
  const quickActionsPopupRef = useRef<HTMLDivElement>(null);

  const { data: properties } = useGetPropertiesListQuery({
    managerId: auth?.user?.current_client_code ?? "",
    page: 1,
    pageSize: 1000,
  });

  const { data: leadFormFieldsData } =
      useGetLeadFormFieldsQuery({});

  const handleAccessibilityClick = () => {
    setOpenAccessibilityDrawer((prev) => !prev);
  };

  const handleQuickActionsToggle = () => {
    setOpenQuickActions((prev) => !prev);
  };

  const handleCloseQuickActions = () => {
    setOpenQuickActions(false);
  };

  useEffect(() => {
     dispatch(setLeadsFormConfig(leadFormFieldsData?.fields));
  }, [leadFormFieldsData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (
        openQuickActions &&
        quickActionsButtonRef.current &&
        quickActionsPopupRef.current &&
        !quickActionsButtonRef.current.contains(target) &&
        !target.closest("[data-quick-actions-popup]")
      ) {
        setOpenQuickActions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openQuickActions]);

  const toggleNotificationDrawer = useCallback(() => {
    dispatch(toggleDrawer());
  }, [dispatch]);

  const hasUnseenNotifications = useMemo(() => {
    return Object.values(notificationState.tabs).some(
      (tab) => tab.unread_events_count > 0
    );
  }, [notificationState.tabs]);

  const handleOpenAddContentDrawer = () => {
    setAddContentDrawerOpen(true);
  };

  return (
    <header className="bg-verba-pearl dark:bg-verba-pearl-dk pr-[12px] py-[7px] flex items-center justify-between gap-[10px]">
      {/* min-w-0: as a flex item this slot defaults to min-width:auto, so it refuses to shrink below
          its content. Any page putting wide content in here (Analytics' board tab strip) pushed the
          icon group on the right straight out of the header — and because <main> is overflow-auto,
          the whole page frame then scrolled sideways, dragging the sidebar and page title off-screen.
          min-w-0 lets the slot shrink so the icons stay put and inner scroll containers can engage. */}
      <div className="w-full min-w-0 flex-1">{children}</div>
      <div className="flex items-center gap-[10px]">
        <div className="relative" ref={quickActionsPopupRef}>
          <button
            ref={quickActionsButtonRef}
            onClick={handleQuickActionsToggle}
            className="w-[36px] h-[36px] rounded-full flex flex-col items-center justify-center border border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
            aria-label="Quick actions"
            aria-expanded={openQuickActions}
            aria-haspopup="menu"
          >
            <AddIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
          <QuickActionsPopup
            isOpen={openQuickActions}
            onClose={handleCloseQuickActions}
            handleOpenAddContentDrawer={handleOpenAddContentDrawer}
          />
        </div>
        <button
          onClick={handleAccessibilityClick}
          className="w-[36px] h-[36px] rounded-full flex flex-col items-center justify-center border border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
        >
          <AccessibilityIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
        </button>
        <button
          className="relative w-[36px] h-[36px] rounded-full flex flex-col items-center justify-center border border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
          onClick={toggleNotificationDrawer}
        >
          <NotificationBellIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
          {hasUnseenNotifications && (
            <span className="absolute top-2.5 right-2.5 w-[5px] h-[5px] bg-red-500 rounded-full" />
          )}
        </button>
        <button
          className="w-[36px] h-[36px] rounded-full flex flex-col items-center justify-center border border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
          onClick={() => setOpenProfileCard(!openProfileCard)}
          data-profile-button
        >
          <UserIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
        </button>{" "}
      </div>
      {openProfileCard && (
        <ProfileCard
          userData={userData as UserData}
          setOpenProfileCard={setOpenProfileCard}
          openProfileCard={openProfileCard}
          setOpenEmailSignatureModal={setOpenEmailSignatureModal}
          setOpenAgentAvailabilityLeavesModal={
            setOpenAgentAvailabilityLeavesModal
          }
          data-profile-button
        />
      )}
      <AccessibilityDrawer
        openDrawer={openAccessibilityDrawer}
        setOpenDrawer={setOpenAccessibilityDrawer}
        size="315px"
      />
      <EmailSignatureModal
        open={openEmailSignatureModal}
        onOpenChange={setOpenEmailSignatureModal}
        onOpenDarkModePreview={(html: string) => {
          setPreviewSignatureHtml(html);
          setOpenEmailSignatureDarkModePreview(true);
        }}
        isEmailSignatureDarkModePreviewOpen={openEmailSignatureDarkModePreview}
      />
      <EmailSignatureDarkModePreview
        open={openEmailSignatureDarkModePreview}
        onOpenChange={setOpenEmailSignatureDarkModePreview}
        signatureHtml={previewSignatureHtml}
      />
      <AgentAvailabilityLeavesModal
        open={openAgentAvailabilityLeavesModal}
        onClose={() => setOpenAgentAvailabilityLeavesModal(false)}
      />
      <AddContentDrawer
        open={addContentDrawerOpen}
        onClose={() => setAddContentDrawerOpen(false)}
        properties={properties}
      />
    </header>
  );
};

export default Header;
